import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { CellMultiSelect } from '@common/ui/selection';
import { Heading } from '@common/ui/typography/Heading';
import { Paragraph } from '@common/ui/typography/Paragraph';
import { useEventEmit } from '@common/utils/events';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import {
  isStorableUser,
  readStoredUser,
  reportRefusedUser,
} from '@entities/auth/utils';
import * as dicts from '@entities/profile/dicts';

import { usePostProfileUpdate } from '@services/hooks/UsersService';

const interestsSchema = z.object({
  interests: z.array(z.string()).nonempty('Введите ваши интересы'),
});

type FormData = z.infer<typeof interestsSchema>;

export const Interests = () => {
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const navigate = useNavigate();

  const { user, setUser } = useAuthStore();
  const profile = readStoredUser(user);
  const { trigger: triggerPostProfileUpdate } = usePostProfileUpdate();

  const [isSaving, setIsSaving] = useState(false);
  // Holds off a second save before the disabled button renders. Released only
  // when a save fails: a saved form leaves the screen.
  const isSavingRef = useRef(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      interests: profile.interests,
    },
    resolver: zodResolver(interestsSchema),
  });

  const onSubmit = async (data: FormData) => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const updatedUser = await triggerPostProfileUpdate({
        interests: data.interests,
      });

      if (isStorableUser(updatedUser)) {
        setUser(updatedUser);
        navigate({ to: '/profile/edit' });
        emit('info', {
          title: 'Данные обновлены',
        });
        return;
      }

      // An answer without a user is a failed save: the stored user stays.
      reportRefusedUser('Interests', updatedUser);
    } catch {
      // A failed request: handled below, as a save without a user is.
    }

    emit('error', {
      title: 'Данные не загрузились',
      description: 'Попробуйте ещё раз',
    });
    isSavingRef.current = false;
    setIsSaving(false);
  };

  useEffect(() => {
    tabbar.setBottomSheet(
      <BottomSheet
        buttons={[
          <Button
            testId="interests-save-button"
            disabled={isSaving}
            loading={isSaving}
            onClick={() => handleSubmit(onSubmit)()}
          >
            Сохранить
          </Button>,
        ]}
      />,
    );
  }, [isSaving]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Heading level={2} align="center">
        Интересы
      </Heading>
      {!errors.interests && (
        <Paragraph variant="secondary" align="center" compact>
          Выбери интересы, чтобы найти людей со схожими увлечениями
        </Paragraph>
      )}
      {errors.interests && (
        <Paragraph variant="negative" align="center" compact>
          {errors.interests?.message}
        </Paragraph>
      )}

      <Card
        testId="profile-interests-form"
        transparent
        style={{ padding: '0 16px' }}
      >
        <Controller
          name="interests"
          control={control}
          render={({ field }) => (
            <CellMultiSelect
              value={field.value}
              onChange={(value) => field.onChange(value)}
              items={Object.keys(dicts.interestDict).map((key) => ({
                value: key,
                label: dicts.interestDict[key],
              }))}
            />
          )}
        />
      </Card>
    </form>
  );
};
